import React, { useState, useEffect, useContext, useMemo } from "react";
import PropTypes from "prop-types";
//
import { useNavigate } from "react-router";
import { useIntl } from "react-intl";
//
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MinimizeIcon from "@mui/icons-material/Minimize";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@material-ui/styles";
// component, hooks
// @ts-ignore
import { isArray } from "lodash";
import isObject from "lodash/isObject";
import DraggableWindow from "../../../../../common/components/draggable/DraggableWindow";
import BasicTable from "./template/BasicTable";
import useDraggable from "./useDraggable";
import ButtonMain from "../../../../../common/components/button/ButtonMain";
// store
import { StoreContext } from "../../../../../store/StoreProvider";
import Act from "../../../../../store/actions";
// config,utils
import { SNA_WRAPPER_DIV_ID, SNA_MAIN_ID } from "../../../config";
import { EVENT_TYPE_CLASS } from "../../../../../config/config.sna";
import { headers } from "./option";
import {
  mergeDiaryEvent,
  formatEventList,
  splitVal,
  getLimitOffset,
} from "./helper";
import getHashValue from "../../../../../common/utils/getHashValue";
import "./diaryInfo.scss";
import { getPublicDataSnaDetail } from "../../../service/datasetService";

//

// style
const StyledBox = styled(Box)(({ theme }) => ({
  "&:hover": {
    cursor: "pointer",
  },
}));

/**
 *
 * @type {null|import("../../../../../common/utils/snaAnalysis/snaAnalysis").default}
 */
const graph = null;

/**
 * react-rnd 物件
 * @typedef RndRef {object}
 * @property {function({width:number,height:number}):void} updateSize
 * @property {function({x:number,y:number}):void} updatePosition
 */

/**
 * react-rnd ref
 * @type {null|RndRef}
 */
let rndRef = null;

const defaultSize = {
  width: 200,
  height: 40,
};

const defaultPosition = {
  x: 0,
  y: 0,
};

const defaultMaxSize = {
  width: 1000,
  height: 600,
};

const defaultBounds = {
  ...defaultPosition,
  width: 800,
  height: 500,
};

const DRAG_HANDLER_CLASS = "diaryinfo_drag_handler";

/**
 * 使用 拖曳視窗,顯示分析結果(page0)及分析標的說明(page1)
 * @returns {JSX.Element}
 * @constructor
 */
const DiaryInfo = () => {
  // props

  // route,intl
  const intl = useIntl();
  const navigate = useNavigate();
  // store

  // @ts-ignore
  const [state, dispatch] = useContext(StoreContext);
  const { sna } = state;
  const { snaDisplay, diaryInfo, dataset, filter } = sna;
  const { focusNodes, memoSnaWidth, memoSnaHeight } = snaDisplay || {};
  const { eventList, curPageRows, displayWindow, startSearch } = diaryInfo;
  const { eventTypes, filterEventTypeOn } = filter || {};

  // local state
  const [rows, setRows] = useState([]);
  // DraggableWindow component 的 default props
  const dragDefault = {
    ...defaultSize,
    ...defaultPosition,
  };
  // dragSize updated
  const [dragSize, setDragSize] = useState({
    ...defaultSize,
  });
  // dragPosition updated
  const [dragPosition, setDragPosition] = useState({
    ...defaultPosition,
  });
  // 邊界: DraggableWindow 的邊界 {x:number,y:number,width:number,height:number}
  const [bounds, setBounds] = useState({
    ...defaultBounds,
  });
  const [pageIdx, setPageIdx] = useState(0);
  const [error, setError] = useState(null);

  /**
   * dataChangeSignal(非常重要的 state):
   * 若使用者切換選取的人物時, curPage 的JSON.stringify(事件 id)皆相同時,就不會 fetch API 第二次
   * 故建立 dataChangeSignal, 只要 eventList 變更, 此 value 就會改變
   */
  const [dataChangeSignal, setDataChangeSignal] = useState("12345");

  // hook
  const {
    onDrag,
    onDragStop,
    onResize,
    askForWindowOpen,
    setDragMiniSize,
    setDragMaxSize,
  } = useDraggable({
    dragPosition,
    setDragPosition,
    dragSize,
    setDragSize,
    bounds,
    rndRef,
    dragEles: [DRAG_HANDLER_CLASS],
    dispatch,
    intl,
    defaultSize,
    defaultMaxSize,
    memoSnaWidth,
    memoSnaHeight,
  });

  // 當 sna 繪圖區的 width 及 height 改變時,設定 bounds
  useEffect(() => {
    if (!(memoSnaWidth == null) && !(memoSnaHeight == null)) {
      setBounds({
        ...bounds,
        width: memoSnaWidth,
        height: memoSnaHeight,
      });
      // 將拖曳視窗放到最大
      if (displayWindow) {
        setDragMaxSize();
      }
    }
  }, [memoSnaWidth, memoSnaHeight]);

  useEffect(() => {
    // 若使用者有 focus nodes 且尚未開啟視窗, 則詢問是否要開啟視窗
    if (isArray(focusNodes) && focusNodes.length > 0) {
      if (!displayWindow) {
        // 關閉此功能,因為每選取一個人物,就會詢問一次,太過頻繁
        // askForWindowOpen();
      }
    }
  }, [focusNodes]);

  useEffect(() => {
    // 若開啟視窗,則最大化
    if (displayWindow) {
      setDragMaxSize();
    }
  }, [displayWindow]);

  // 依據被使用者 focus 的節點來過濾事件
  useEffect(() => {
    if (isArray(eventList)) {
      // 清空資料
      setRows(null);
      // 啟動搜尋
      dispatch({
        type: Act.SNA_DIARY_INFO_START_SEARCH,
        payload: true,
      });
      // 頁碼回到第一頁
      dispatch({
        type: Act.SNA_DIARY_INFO_CUR_PAGE,
        payload: 1,
      });
      const hashValue = getHashValue(
        (eventList || []).concat(focusNodes || [])
      );
      setDataChangeSignal(hashValue);
      if (isArray(focusNodes) && focusNodes.length > 0) {
        const focusNodesId = focusNodes.map((o) => o.id);
        // 融合事件 & 過濾資料(保留有包含節點人物的資料)
        const eventListMerged = mergeDiaryEvent(
          formatEventList(eventList, dataset.public)
        ).filter((event) => {
          let findPersonCount = 0;
          focusNodesId.forEach((id) => {
            if (event.perLabels.indexOf(id) >= 0) findPersonCount += 1;
          });
          if (focusNodes.length === 1) {
            // 只要 event.perLabels 中有此節點人物,就回傳 true
            return findPersonCount > 0;
          }
          // event.perLabels 只少要出現 2 個節點人物,才回傳 true
          return findPersonCount >= 2;
        });
        setRows(eventListMerged);
      } else {
        const eventListMerged = mergeDiaryEvent(
          formatEventList(eventList, dataset.public)
        );
        setRows(eventListMerged);
      }
    }
  }, [eventList, focusNodes]);

  // 當 table cur page rows 變更時,fetch API取得詳細資料(content,...)
  useEffect(() => {
    // fetch API 取得更詳細資訊
    const onStart = () => {
      // 啟動搜尋
      dispatch({
        type: Act.SNA_DIARY_INFO_START_SEARCH,
        payload: true,
      });
    };

    /**
     *
     * @param data {*[]}
     */
    const onSuccess = (data) => {
      // 檢查 eventTypeId:每筆資料都要包含 已 checked 的 eventTypeId
      const tmpRows = (rows || [])
        .map((o) => {
          let find = null;
          if (isArray(data)) {
            find = data.find(
              (dt) =>
                // @ts-ignore
                (o?.eventTypeTagsId || "").indexOf(dt.eventTypeTag) >= 0
            );
          }

          if (find) {
            return {
              ...((isObject(o) && o) || {}),
              ...(find || {}),
              persons: splitVal(find?.persons).join(","),
              personTagLabel: splitVal(find?.personTagLabel).join(","),
            };
          }
          return { ...((isObject(o) && o) || {}) };
        })
        .map((o) => ({
          ...o,
          eventTypeName2:
            o.eventTypesName.indexOf(o.eventTypeName2) >= 0
              ? ""
              : o.eventTypeName2,
        }));
      setRows(tmpRows);
      // 停止搜尋
      dispatch({
        type: Act.SNA_DIARY_INFO_START_SEARCH,
        payload: false,
      });
    };

    const onError = (/** @type {React.SetStateAction<null>} */ err) => {
      setError(err);
      // 停止搜尋
      dispatch({
        type: Act.SNA_DIARY_INFO_START_SEARCH,
        payload: false,
      });
    };
    /*
    依據 該頁的資料, fetch API 取得詳細資訊
    curPageResult 是 parent 篩選後預測該頁的資料(相對不精確)
    curPageRows 是 Table 過濾,排序後該頁的資料(精確)
    故使用 curPageRows 來 fetch API 取得詳細資訊
    */
    let ids = [];
    // @ts-ignore
    if (isArray(curPageRows) && curPageRows.length > 0) {
      // @ts-ignore
      // ids = curPageRows.map(o => o.eventTypeTag);

      ids = curPageRows.reduce((acc, cur) => {
        if (isArray(cur.eventTypeTags)) {
          return acc.concat(
            cur.eventTypeTags.map(
              (/** @type {{ eventTypeTag: any; }} */ o) => o.eventTypeTag
            )
          );
        }
        return acc;
      }, []);
    }
    const uniqIds = Array.from(new Set(ids));
    if (ids.length > 0) {
      // 只需要撈開放日誌的資訊,團隊或私人的詳細資料暫不處理
      const publicDatasetG = (dataset.public || [])
        .filter((item) => item.checked)
        .map((o) => o.graph);

      // 若要過濾啟用 事件分類功能,則要帶入 有 checked 的 親屬事件 evenType.id,方便 api 僅撈出符合的親屬事件及其人物
      // e.g.[dingCuoInLaw,dingCuoKinship]
      const inKinshipIds = filterEventTypeOn.initial
        ? eventTypes.initial
            .filter(
              (o) => o.checked && o.type === EVENT_TYPE_CLASS.KinshipEventType
            )
            .map((o) => o.id)
        : [];

      // 可以同時撈語意分析事件的詳細資料 & 親屬事件的詳細資料
      getPublicDataSnaDetail(
        {
          ids: uniqIds,
          graphs: publicDatasetG,
          inKinship: inKinshipIds.join(","),
        },
        onStart,
        onSuccess,
        onError
      );
    }
  }, [curPageRows]);

  // 縮小視窗
  const onClickMini = () => {
    setDragMiniSize();
  };

  // 放大視窗
  const onClickMax = () => {
    setDragMaxSize();
  };

  // 關閉視窗
  const onClickClose = () => {
    dispatch({
      type: Act.SNA_DIARY_INFO_DISPLAY_WINDOW,
      payload: false,
    });
  };

  // 拖曳視窗的 header
  const getDragHeader = (
    <Box display="flex" justifyContent="space-between">
      <Box display="flex" justifyContent="flex-start">
        <IconButton
          className={DRAG_HANDLER_CLASS}
          sx={{
            "&:hover": {
              cursor: "grab",
            },
          }}
        >
          <DragHandleIcon />
        </IconButton>
        {/* <IconButton onClick={onClickMini}> */}
        {/*  <MinimizeIcon /> */}
        {/* </IconButton> */}
        {/* <IconButton onClick={onClickMax}> */}
        {/*  <CropSquareIcon /> */}
        {/* </IconButton> */}
      </Box>
      <Box>
        <IconButton onClick={onClickClose}>
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );

  /**
   * 取得 draggableWindow title
   * @returns {*|string}
   */
  const getTitle = () => focusNodes.map((o) => o.id).join(",");

  const getDragTitle = useMemo(
    () => (
      <Box py={1} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="h6" textAlign="center">
          焦點人物：{getTitle()}
        </Typography>
        {pageIdx === 0 && (
          <IconButton onClick={() => setPageIdx(1)}>
            <HelpOutlineIcon />
          </IconButton>
        )}
      </Box>
    ),
    [focusNodes, pageIdx]
  );

  // 使用 useMemo 會有 side effect
  // const getPage0 = useMemo(
  //   () => (
  //     // 分析結果
  //     <Box>
  //       {getDragHeader}
  //       <Box py={1} px={3} bgcolor="#fff">
  //         {getDragTitle}
  //         <BasicTable
  //           headers={headers}
  //           rows={rows}
  //           dataChangeSignal={dataChangeSignal}
  //         />
  //       </Box>
  //     </Box>
  //   ),
  //   [headers, rows, memoSnaWidth, memoSnaHeight, focusNodes, pageIdx, startSearch]
  // );

  // 使用 useMemo, 無法及時顯示 BasicTable 的其他狀態(e.g.loading)
  // 所以不要使用 useMemo
  const getPage0 = (
    <Box>
      {getDragHeader}
      <Box py={1} px={3} bgcolor="#fff">
        {getDragTitle}
        <BasicTable
          headers={headers}
          rows={rows}
          dataChangeSignal={dataChangeSignal}
        />
      </Box>
    </Box>
  );

  const pageTextList = [
    { text: "選取人物以過濾日誌事件。", variant: "body1" },
    { text: "請點擊網絡圖中的人物以選取人物。", variant: "body1" },
    {
      text: "當選擇一個人物時，會顯示此人物的所有事件;當選擇二個以上人物時，會顯示至少有二個人物出現的事件。",
      variant: "body1",
    },
  ];

  const getPage1 = useMemo(
    () => (
      // 分析項目的說明文字
      <Box>
        {getDragHeader}
        <Box py={1} px={3} bgcolor="#fff">
          {getDragTitle}
          <Box py={1}>
            <ButtonMain
              buttonProps={{
                text: intl.formatMessage({
                  id: "common.goto.previous.page",
                  defaultMessage: "回上一頁",
                }),
                variant: "text",
                startIcon: <ArrowBackIcon />,
                onClick: () => setPageIdx(0),
              }}
            />
          </Box>
          <Box py={2} minHeight={500}>
            {pageTextList.map((o, idx) => (
              <Typography key={idx.toString()} variant={o.variant}>
                {o.text}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>
    ),
    [pageIdx]
  );

  const pages = [getPage0, getPage1];
  const getElement = () => pages?.[pageIdx] || null;

  /**
   * 取得 react-rnd(draggable window)的 ref
   * @param ref {null|RndRef}
   */
  const onDragWindowReady = (ref) => {
    rndRef = ref;
  };

  return (
    <StyledBox
      position="relative"
      left={0}
      top={0}
      height={0}
      display={displayWindow ? "flex" : "none"}
      justifyContent="flex-end"
    >
      <DraggableWindow
        onReady={onDragWindowReady}
        element={getElement()}
        rndDefault={dragDefault}
        // //若有設定 boundingBoxClassName, 則取消 onDrag, onDragStop
        // onDrag={onDrag}
        // onDragStop={onDragStop}
        onResize={onResize}
        minWidth={defaultSize.width}
        minHeight={defaultSize.height}
        maxWidth={defaultMaxSize.width}
        maxHeight={defaultMaxSize.height}
        boundingBoxClassName={`.${SNA_MAIN_ID}`}
        dragHandleClassName={DRAG_HANDLER_CLASS}
      />
    </StyledBox>
  );
};

DiaryInfo.propTypes = {};

DiaryInfo.defaultProps = {};

export default DiaryInfo;
